/**
 * streamPreview.js — F-415-STREAM: shape the story text streaming onto the generating screen.
 *
 * The Whole Tale arrives with its title as the FIRST LINE and the story after it. While the text
 * is still streaming we only ever have a PREFIX of that, so this decides — honestly, per poll —
 * what is title and what is body, with the same rule the finished story uses
 * (`extractTitleContent` in App.js: first line is the title only once a second line proves it).
 *
 * DELIBERATELY MINIMAL, and the reason is the invariant (`STREAM-PREVIEW-NEVER-A-STORY-1`): the
 * preview is raw accumulating model text. No dash scrubbing, no label scrubbing, no gates — those
 * run on the FINISHED story, which replaces this the moment it exists. Transforming text
 * mid-stream would be showing the teller something no model wrote, which is the substitute shape
 * (F-334) one layer down.
 *
 * Pure and extracted because App.js cannot be loaded in Jest (react-pdf ESM) — the same reason
 * generationTimeout.js and saveGuards.js exist. See .claude/rules/app-multidomain.md.
 */

/**
 * @param {string} raw  the accumulated text streamed so far
 * @returns {{ title: string|null, paragraphs: string[] }}
 *   title      the first line, once a second non-empty line has arrived to prove it is one
 *              (< 60 chars, mirroring extractTitleContent's core check); null until then
 *   paragraphs the body split on blank-line boundaries, in arrival order; the last one is
 *              usually mid-sentence, and that is the point
 */
export function formatStreamPreview(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return { title: null, paragraphs: [] };

  const lines = raw.split('\n');
  const firstNonEmpty = lines.findIndex(l => l.trim());
  const first = lines[firstNonEmpty].replace(/^#+\s*/, '').replace(/\*+/g, '').trim();
  const restLines = lines.slice(firstNonEmpty + 1);
  const restText = restLines.join('\n').trim();

  // The first line is only a TITLE once a second non-empty line exists — before that it may be
  // the opening sentence still being typed, and promoting it early would show a heading that
  // then demotes itself. Same `lines.length > 1` rule as the finished story's extractor.
  const looksLikeTitle = restText.length > 0 && first.length < 60 && !first.includes('?');

  const body = looksLikeTitle ? restText : raw.trim();
  const paragraphs = body
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);

  return { title: looksLikeTitle ? first : null, paragraphs };
}
